import { createSlice } from "@reduxjs/toolkit";
import { defaultRequestBody, DefaultRequestBodyType } from "../../../constant/document";
import { closeCreateUsersModal, closeDeleteUsersModal, closeOrganizationModal, createUsers, deleteUsers, fetchPaginateUsers, fetchRefreshUsers, fetchUsers, openCreateUsersModal, openDeleteUsersModal, openOrganizationModal, resetUsersTable, setCurrentRecord, setUsersAutoRefresh, stopUsersAutoRefresh, switchOffUser, switchOnUser, switchUsersAutoFresh, updateUsersRequestBody } from "./UsersAction";

export interface PaginationType {
  page: number;
  pageSize: number;
  hasMore: boolean;
  loading: boolean;
}
interface AutoRefreshStateType {
  autoRefresh: boolean;
  currentIntervalId: NodeJS.Timer | undefined;
  error: string | undefined;
  loading: boolean;
}

interface UsersPageStateType {
  data: any;
  loading: boolean;
  error: string | undefined;
  currentRequestBody: DefaultRequestBodyType;
  pagination: PaginationType;
  autoRefreshUsersState: AutoRefreshStateType;
  success: boolean;
  message: string;
  deleteUsersLoading: boolean;
  createUsersLoading: boolean;
  switchUserLoading: boolean;
  //Modal
  createUsersModalState: boolean;
  deleteUsersModalState: boolean;
  organizationModalState: boolean;
  currentRecord: any;
}

const UsersInitialState: UsersPageStateType = {
  data: [],
  loading: false,
  error: undefined,
  currentRequestBody: defaultRequestBody,
  deleteUsersLoading: false,
  createUsersLoading: false,
  switchUserLoading: false,
  pagination: {
    page: 2,
    pageSize: 30,
    hasMore: true,
    loading: false,
  },
  autoRefreshUsersState: {
    autoRefresh: false,
    currentIntervalId: undefined,
    error: "",
    loading: false,
  },
  success: false,
  message: "",

  //Modal
  createUsersModalState: false,
  deleteUsersModalState: false,
  organizationModalState: false,
  currentRecord: {},
};

const UsersPageSlice = createSlice({
  initialState: UsersInitialState,
  name: "transcribe",
  reducers: {},
  extraReducers(builder) {
    builder
      //Fetch Users data from API
      .addCase(fetchUsers.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })

      //Infinite Scrolling
      .addCase(fetchPaginateUsers.pending, (state, action) => {
        state.pagination.loading = true;
      })
      .addCase(fetchPaginateUsers.fulfilled, (state, action) => {
        state.pagination.page = state.pagination.page + 1;
        state.data = state.data.concat(action.payload);
        state.pagination.hasMore = action.payload.length === state.pagination.pageSize;
        state.pagination.loading = false;
      })
      .addCase(fetchPaginateUsers.rejected, (state, action) => {
        state.error = action.error.message;
        state.pagination.loading = false;
      })

      //Search Users
      .addCase(updateUsersRequestBody, (state, action) => {
        state.currentRequestBody = action.payload;
      })
      .addCase(resetUsersTable, (state, action) => {
        return {
          ...UsersInitialState,
          data: state.data,
        };
      })

      //  Refresh
      .addCase(fetchRefreshUsers.pending, (state, action) => {
        state.autoRefreshUsersState.loading = true;
      })
      .addCase(fetchRefreshUsers.fulfilled, (state, action) => {
        state.data = action.payload;
        state.autoRefreshUsersState.loading = false;
      })
      .addCase(fetchRefreshUsers.rejected, (state, action) => {
        state.autoRefreshUsersState.error = action.error.message;
        state.autoRefreshUsersState.loading = false;
      })

      //Auto Refresh
      .addCase(setUsersAutoRefresh, (state, action) => {
        state.autoRefreshUsersState.currentIntervalId = action.payload.intervalId;
      })
      .addCase(switchUsersAutoFresh, (state, action) => {
        state.autoRefreshUsersState.autoRefresh = action.payload.autoRefresh;
      })
      .addCase(stopUsersAutoRefresh, (state, action) => {
        clearInterval(state.autoRefreshUsersState.currentIntervalId);
      })

      .addCase(createUsers.pending, (state, action) => {
        state.createUsersLoading = true;
      })
      .addCase(createUsers.fulfilled, (state, action) => {
        state.createUsersLoading = false;
      })
      .addCase(createUsers.rejected, (state, action) => {
        state.createUsersLoading = false;
        state.error = action.error.message;
      })
      .addCase(deleteUsers.pending, (state, action) => {
        state.deleteUsersLoading = true;
      })
      .addCase(deleteUsers.fulfilled, (state, action) => {
        state.deleteUsersLoading = false;
      })
      .addCase(deleteUsers.rejected, (state, action) => {
        state.deleteUsersLoading = false;
        state.error = action.error.message;
      })
      .addCase(switchOnUser.pending, (state, action) => {
        state.switchUserLoading = true;
      })
      .addCase(switchOnUser.fulfilled, (state, action) => {
        state.switchUserLoading = false;
      })
      .addCase(switchOnUser.rejected, (state, action) => {
        state.switchUserLoading = false;
        state.error = action.error.message;
      })
      .addCase(switchOffUser.pending, (state, action) => {
        state.switchUserLoading = true;
      })
      .addCase(switchOffUser.fulfilled, (state, action) => {
        state.switchUserLoading = false;
      })
      .addCase(switchOffUser.rejected, (state, action) => {
        state.switchUserLoading = false;
        state.error = action.error.message;
      })

      //Modal
      .addCase(openCreateUsersModal, (state, action) => {
        state.createUsersModalState = true;
      })
      .addCase(closeCreateUsersModal, (state, action) => {
        state.createUsersModalState = false;
      })
      .addCase(openDeleteUsersModal, (state, action) => {
        state.deleteUsersModalState = true;
      })
      .addCase(closeDeleteUsersModal, (state, action) => {
        state.deleteUsersModalState = false;
      })
      .addCase(openOrganizationModal, (state, action) => {
        state.organizationModalState = true;
      })
      .addCase(closeOrganizationModal, (state, action) => {
        state.organizationModalState = false;
      })
      .addCase(setCurrentRecord, (state, action) => {
        state.currentRecord = action.payload;
      });
  },
});

export default UsersPageSlice.reducer;
